import React, { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Layout from "../../../components/layout";
import ProcessOrderForm from "../../../components/processOrders/ProcessOrderForm";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  fetchProcessOrderById,
  getViewProcessOrder,
  getProcessOrdersLoading,
  setViewProcessOrder,
} from "../../../features/processOrders/processOrdersSlice";
import { getLoggingUser } from "../../../features/loggingUser/loggingUserSlice";
import { getSelectedBranch } from "../../../features/settings/settingsSlice";
import { useCanAccess } from "../../../hooks/useCanAccess";
import { useProcessDefinitionFromQuery } from "../../../hooks/useProcessDefinitionFromQuery";
import { localizedProcessName } from "../../../lib/utils";
import { dateToInputValue } from "../../../static/datetime";
import { t } from "../../../locales";
import { route } from "../../../static/route";

const ProcessOrderEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const order = useAppSelector(getViewProcessOrder);
  const loading = useAppSelector(getProcessOrdersLoading);
  const loggingUser = useAppSelector(getLoggingUser);
  const selectedBranch = useAppSelector(getSelectedBranch);
  const { processDefinition, loading: definitionLoading } = useProcessDefinitionFromQuery(t.processOrders.processNotSupported);

  useEffect(() => {
    if (loggingUser?.permissions && selectedBranch) {
      if (!canAccess("process-orders.update")) {
        navigate(route.dashboard);
        return;
      }
      if (id) {
        dispatch(fetchProcessOrderById(Number(id)));
      }
    }
    return () => {
      dispatch(setViewProcessOrder(null));
    };
  }, [loggingUser, selectedBranch, id]);

  const defaultValues = order
    ? {
        party_id: order.party_id || 0,
        date: dateToInputValue(order.date),
        items: order.items?.map((it) => ({
          sub_process_id: it.sub_process_id || 0,
          item_id: it.item_id || 0,
          item_size_id: it.item_size_id ?? 0,
          cutting_size: it.cutting_size ?? "",
          // Hydrates the Material dropdown from the loaded size's own
          // material relation, so both cascading dropdowns start pre-filled.
          material_id: it.size?.material_id ?? 0,
          size_id: it.size_id ?? 0,
          weight: it.weight ?? "",
          qty: it.qty,
        })) || [{ sub_process_id: 0, item_id: 0, item_size_id: 0, cutting_size: "", material_id: 0, size_id: 0, weight: "", qty: "" }],
      }
    : undefined;

  const defaultPartyOption = order?.party
    ? { value: order.party.id, label: order.party.firm_name || order.party.name }
    : null;

  return (
    <Layout>
      <div className="px-2 py-4 md:px-6 md:py-6">
        <nav className="text-sm font-semibold text-gray-500 mb-4">
          <Link to={route.dashboard} className="text-brand-dark hover:underline">
            {t.processOrders.breadcrumbHome}
          </Link>
          <span className="mx-2">&rsaquo;</span>
          {processDefinition && (
            <>
              <Link to={`${route.processOrders}?process=${processDefinition.slug}`} className="text-brand-dark hover:underline">
                {t.processOrders.breadcrumbList}
              </Link>
              <span className="mx-2">&rsaquo;</span>
            </>
          )}
          <span className="text-gray-500">{t.processOrders.editTitle}</span>
        </nav>
        <h1 className="text-2xl font-bold text-brand-dark mb-6">
          {processDefinition ? `${localizedProcessName(processDefinition)} — ${t.processOrders.editTitle}` : t.processOrders.editTitle}
        </h1>
        {definitionLoading || !processDefinition || (loading && !order) ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#113F67]" />
          </div>
        ) : order ? (
          <ProcessOrderForm
            processDefinition={processDefinition}
            editId={order.id}
            defaultValues={defaultValues}
            defaultPartyOption={defaultPartyOption}
            defaultMode={order.mode}
            editType={order.type}
          />
        ) : (
          <p className="text-center text-gray-500 py-12">{t.processOrders.noRecords}</p>
        )}
      </div>
    </Layout>
  );
};

export default ProcessOrderEditPage;
